import { Framework, IHistoryItem } from '@/framework';
import { traverseTrees } from '@/utils/tree-kit';
import { SizeUnitType } from '@/utils/unit-kit';
import { ILayerProps, IPoint, ISize, ResizeOperateType } from '@/version-types';
import { type QuickAdjustOperate } from '../operate/quick-adjust-operate';
import { type QuickResizeOperate } from '../operate/quick-resize-operate';
import type { StageHistoryExtensionContext, StageHistoryExtensions } from '../operation-events';
import { StageOperateImpl } from '../stage-operate';
import { historyQuickAdjustOperate, historyQuickResizeOperate } from '../type';

interface IStageHistoryQuickAdjustData {
    /** 目标舞台的原始序号。 */
    originIndex: number;
    /** 调整前的舞台尺寸，单位为编辑器逻辑像素。 */
    oldSize: ISize;
    /** 调整后的舞台尺寸，单位为编辑器逻辑像素。 */
    newSize: ISize;
    /** 写入历史时使用的编辑器尺寸单位。 */
    unit: SizeUnitType;
    /** 本次舞台 resize 的手柄方向。 */
    operateType: ResizeOperateType;
    /** 上层插件按命名空间写入的历史扩展数据。 */
    extensions?: StageHistoryExtensions;
}

interface IStageHistoryQuickResizeData {
    /** 目标舞台的原始序号。 */
    originIndex: number;
    /** 调整前的舞台高度，单位为编辑器逻辑像素。 */
    oldHeight: number;
    /** 调整后的舞台高度，单位为编辑器逻辑像素。 */
    newHeight: number;
    /** 上层插件按命名空间写入的历史扩展数据。 */
    extensions?: StageHistoryExtensions;
}

/**
 * 单画布舞台快速调整
 */
export class QuickStageHistory extends Framework {
    /** 协同舞台调整开始时记录的图层原始位置。 */
    private syncOriginPosition: Map<string, IPoint> = new Map();

    constructor(
        /** 当前编辑器实例标识，用于隔离历史记录。 */
        private id: string,
    ) {
        super(id);
        this.registerQuickAdjustOperate();
        this.registerQuickResizeOperate();
    }

    private registerQuickResizeOperate() {
        this.framework.service.history.register(historyQuickResizeOperate, {
            owner: 'plugin/user-editor/stage-operate',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IStageHistoryQuickResizeData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { originIndex, oldHeight, newHeight, extensions } = historyItem.data.data;
                this.framework.service.frame.delActiveLayer();
                if (isUndo) {
                    this.framework.service.registration
                        .getInstance<QuickResizeOperate>('QuickResizeOperate')
                        .doResize(originIndex, oldHeight);
                    this.emitApplyHistoryExtensions('resize', extensions, isRedo, isUndo, 'local');
                }
                if (isRedo) {
                    this.framework.service.registration
                        .getInstance<QuickResizeOperate>('QuickResizeOperate')
                        .doResize(originIndex, newHeight);
                    this.emitApplyHistoryExtensions('resize', extensions, isRedo, isUndo, 'local');
                }
                this.framework.service.frame.setActiveIndex(this.framework.service.frame.frameIdToIndex(originIndex));
                this.framework.service.frame.setPickActiveFrame(true);
            },
            onAfterWrite: () => {
                return {
                    message: [],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IStageHistoryQuickResizeData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IStageHistoryQuickResizeData>, isRedo, isUndo, store) => {
                const { originIndex, oldHeight, newHeight } = historyItem.data.data;
                const size = this.framework.service.frame.getSize(originIndex);
                if (isUndo) {
                    const isRender = this.framework.service.frame.checkFrameIsRender(originIndex);
                    historyItem.isDirty = !isRender || !(size.height === newHeight);
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        const isRender = this.framework.service.frame.checkFrameIsRender(originIndex);
                        historyItem.isDirty = !isRender || !(size.height === oldHeight);
                    }
                }
            },
            onMessage: (message: [IStageHistoryQuickResizeData], isRedo, isUndo, isWrite) => {
                if (isWrite) return;
                const { originIndex, oldHeight, newHeight, extensions } = message[0];
                this.framework.service.frame.tryRemoveActiveLayer(originIndex);
                if (isUndo) {
                    this.framework.service.registration
                        .getInstance<QuickResizeOperate>('QuickResizeOperate')
                        .doResize(originIndex, oldHeight);
                    this.emitApplyHistoryExtensions('resize', extensions, isRedo, isUndo, 'collaboration');
                }
                if (isRedo) {
                    this.framework.service.registration
                        .getInstance<QuickResizeOperate>('QuickResizeOperate')
                        .doResize(originIndex, newHeight);
                    this.emitApplyHistoryExtensions('resize', extensions, isRedo, isUndo, 'collaboration');
                }
            },
            onSend: (data: {
                originIndex: number;
                oldHeight: number;
                newHeight: number;
                isStart: boolean;
                isEnd: boolean;
            }) => {
                return {
                    message: [data.originIndex, data.oldHeight, data.newHeight, data.isStart, data.isEnd],
                    isThrottle: true,
                    force: data.isEnd,
                };
            },
            onReceive: (message: [number, number, number, boolean, boolean]) => {
                const [originIndex, oldHeight, newHeight, isStart, isEnd] = message;
                this.framework.service.frame.tryRemoveActiveLayer(originIndex);
                if (isStart) {
                    this.framework.service.registration.getInstance<StageOperateImpl>('StageOperate').emitResizeStart();
                }
                if (isEnd) {
                    this.framework.service.registration.getInstance<StageOperateImpl>('StageOperate').emitResizeEnd();
                }
                if (!isStart && !isEnd) {
                    this.framework.service.registration.getInstance<StageOperateImpl>('StageOperate').emitResizing({
                        newSize: newHeight,
                        oldSize: oldHeight,
                        operateType: ResizeOperateType.BC,
                        stageIndex: this.framework.service.frame.frameIdToIndex(originIndex),
                    });
                }
                this.framework.service.registration
                    .getInstance<QuickResizeOperate>('QuickResizeOperate')
                    .doResize(originIndex, newHeight);
            },
        });
    }

    private registerQuickAdjustOperate() {
        this.framework.service.history.register(historyQuickAdjustOperate, {
            owner: 'plugin/user-editor/stage-operate',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IStageHistoryQuickAdjustData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { originIndex, oldSize, newSize, unit, operateType, extensions } = historyItem.data.data;
                this.framework.service.frame.delActiveLayer();
                this.framework.service.editor.setUnit(unit);
                if (isUndo) {
                    this.framework.service.registration
                        .getInstance<QuickAdjustOperate>('QuickAdjustOperate')
                        .doAdjust(originIndex, newSize, oldSize, operateType);
                    this.emitApplyHistoryExtensions('adjust', extensions, isRedo, isUndo, 'local');
                }
                if (isRedo) {
                    this.framework.service.registration
                        .getInstance<QuickAdjustOperate>('QuickAdjustOperate')
                        .doAdjust(originIndex, oldSize, newSize, operateType);
                    this.emitApplyHistoryExtensions('adjust', extensions, isRedo, isUndo, 'local');
                }
            },
            onAfterWrite: () => {
                return {
                    message: [],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IStageHistoryQuickAdjustData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IStageHistoryQuickAdjustData>, isRedo, isUndo, store) => {
                const { originIndex, oldSize, newSize } = historyItem.data.data;
                const size = this.framework.service.frame.getSize(originIndex);
                if (isUndo) {
                    const isRender = this.framework.service.frame.checkFrameIsRender(originIndex);
                    historyItem.isDirty =
                        !isRender || !(size.width === newSize.width && size.height === newSize.height);
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        const isRender = this.framework.service.frame.checkFrameIsRender(originIndex);
                        historyItem.isDirty =
                            !isRender || !(size.width === oldSize.width && size.height === oldSize.height);
                    }
                }
            },

            onMessage: (message: [IStageHistoryQuickAdjustData], isRedo, isUndo, isWrite) => {
                if (isWrite) return;
                this.syncOriginPosition.clear();
                const { originIndex, oldSize, newSize, unit, operateType, extensions } = message[0];
                this.framework.service.frame.tryRemoveActiveLayer(originIndex);
                this.framework.service.editor.setUnit(unit);
                if (isUndo) {
                    this.framework.service.registration
                        .getInstance<QuickAdjustOperate>('QuickAdjustOperate')
                        .doAdjust(originIndex, newSize, oldSize, operateType);
                    this.emitApplyHistoryExtensions('adjust', extensions, isRedo, isUndo, 'collaboration');
                }
                if (isRedo) {
                    this.framework.service.registration
                        .getInstance<QuickAdjustOperate>('QuickAdjustOperate')
                        .doAdjust(originIndex, oldSize, newSize, operateType);
                    this.emitApplyHistoryExtensions('adjust', extensions, isRedo, isUndo, 'collaboration');
                }
            },
            onSend: (data: {
                originIndex: number;
                oldSize: ISize;
                newSize: ISize;
                adjustType: ResizeOperateType;
                isStart: boolean;
                isEnd: boolean;
            }) => {
                return {
                    message: [data.originIndex, data.oldSize, data.newSize, data.adjustType, data.isStart, data.isEnd],
                    isThrottle: true,
                    force: data.isEnd,
                };
            },
            onReceive: (message: [number, ISize, ISize, ResizeOperateType, boolean, boolean]) => {
                const [originIndex, oldSize, newSize, adjustType, isStart, isEnd] = message;
                this.framework.service.frame.tryRemoveActiveLayer(originIndex);
                if (isStart) {
                    this.syncOriginPosition.clear();
                    const layers = this.framework.service.frame.getLayers(originIndex);
                    traverseTrees(layers, (layer: ILayerProps) => {
                        this.syncOriginPosition.set(layer.id, {
                            x: layer.left,
                            y: layer.top,
                        });
                    });
                    // 发送舞台尺寸变化前事件
                    this.framework.service.registration.getInstance<StageOperateImpl>('StageOperate').emitResizeStart();
                }
                if (isEnd) {
                    // 发送舞台尺寸变化后事件
                    this.framework.service.registration.getInstance<StageOperateImpl>('StageOperate').emitResizeEnd();
                }
                if (!isStart && !isEnd) {
                    const isVertical = adjustType === ResizeOperateType.TC || adjustType === ResizeOperateType.BC;
                    this.framework.service.registration.getInstance<StageOperateImpl>('StageOperate').emitResizing({
                        newSize: isVertical ? newSize.height : newSize.width,
                        oldSize: isVertical ? oldSize.height : oldSize.width,
                        operateType: adjustType,
                        stageIndex: this.framework.service.frame.frameIdToIndex(originIndex),
                    });
                }
                this.framework.service.registration
                    .getInstance<QuickAdjustOperate>('QuickAdjustOperate')
                    .doAdjust(originIndex, oldSize, newSize, adjustType, this.syncOriginPosition);
            },
        });
    }

    /**
     * 在原业务扩展恢复位置分发通用舞台历史事件。
     * QuickStageHistory 只透传命名空间数据，不解释任何上层插件字段。
     */
    private emitApplyHistoryExtensions(
        operation: StageHistoryExtensionContext['operation'],
        extensions: StageHistoryExtensions | undefined,
        isRedo: boolean,
        isUndo: boolean,
        source: 'local' | 'collaboration',
    ) {
        this.framework.service.registration.getInstance<StageOperateImpl>('StageOperate').emitApplyHistoryExtensions({
            operation,
            extensions: extensions || {},
            isRedo,
            isUndo,
            source,
        });
    }
}
